import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Gem, Layers, Hotel, BadgeCheck, Truck, HeartHandshake,
  Palmtree, Hospital, Home as HomeIcon, BedDouble, Building2, UtensilsCrossed,
  Store, Package, Briefcase, Landmark, BookOpen, Download,
} from "lucide-react";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import EditorialMarquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";
import Testimonials from "@/components/Testimonials";
import { IMG, CATEGORIES, CUSTOMERS } from "@/data/content";

const WHY = [
  { icon: Gem, title: "Quality Materials", desc: "Focused on dependable materials and well-finished textile products." },
  { icon: Layers, title: "Wide Product Range", desc: "A comprehensive selection of bedding, bath and hospitality essentials." },
  { icon: Hotel, title: "Hospitality Focus", desc: "Products designed with hotels, resorts and institutional requirements in mind." },
  { icon: BadgeCheck, title: "Consistent Workmanship", desc: "Professional attention to finishing and product presentation." },
  { icon: Truck, title: "B2B Supply", desc: "Suitable for businesses requiring hospitality textile products at scale." },
  { icon: HeartHandshake, title: "Reliable Service", desc: "Focused on professional communication and dependable customer service." },
];

const INDUSTRIES = [
  { icon: Hotel, label: "Hotels" },
  { icon: Palmtree, label: "Resorts" },
  { icon: Hospital, label: "Hospitals" },
  { icon: HomeIcon, label: "Guest Houses" },
  { icon: BedDouble, label: "Hostels" },
  { icon: Building2, label: "Service Apartments" },
  { icon: UtensilsCrossed, label: "Restaurants" },
  { icon: Store, label: "Hospitality Businesses" },
  { icon: Package, label: "Wholesale Buyers" },
  { icon: Truck, label: "Distributors" },
  { icon: Briefcase, label: "Corporate Buyers" },
  { icon: Landmark, label: "Institutional Buyers" },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-navy">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={IMG.heroBedroom} alt="Luxury hotel bedroom with premium white bedding" className="w-full h-[115%] object-cover" />
        <div className="absolute inset-0 bg-navy/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/15" />
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-44 w-full">
        <FadeIn delay={0.15}>
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-gold" />
            <span className="text-[11px] tracking-[0.34em] uppercase text-ivory/80">
              Amazing Grace — Coimbatore, Tamil Nadu
            </span>
          </div>
        </FadeIn>

        <h1 className="font-serif font-medium tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-ivory leading-[1.02] max-w-5xl">
          <MaskedLine delay={0.3}>Hotel Linen & Hospitality</MaskedLine>
          <MaskedLine delay={0.45}>Textile <span className="italic text-gold">Supplier</span></MaskedLine>
          <MaskedLine delay={0.6}>in India</MaskedLine>
        </h1>

        <FadeIn delay={0.9}>
          <p className="mt-8 text-ivory/80 text-base md:text-lg font-light max-w-xl leading-relaxed">
            Premium hotel bed sheets, towels, bedding and hospitality essentials manufactured in Karamadai, Coimbatore — supplied to hotels, resorts and institutions across India.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mt-10">
            <Link
              to="/products"
              data-testid="hero-explore-products-button"
              className="group inline-flex items-center justify-center gap-2 bg-ivory text-navy text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-gold"
            >
              Explore Our Products
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/quote"
              data-testid="hero-enquire-now-button"
              className="group inline-flex items-center justify-center gap-2 border border-ivory/50 text-ivory text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Enquire Now
            </Link>
          </div>
        </FadeIn>
      </div>

      <FadeIn delay={1.3} className="absolute bottom-8 right-8 hidden md:block">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[10px] tracking-[0.3em] uppercase text-ivory/50 [writing-mode:vertical-rl]">Scroll</span>
          <motion.span
            className="w-px h-14 bg-gradient-to-b from-gold to-transparent"
            animate={{ scaleY: [1, 0.5, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </FadeIn>
    </section>
  );
};

const Intro = () => (
  <section className="py-24 md:py-32">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
      <div>
        <SectionHeader
          number="01"
          eyebrow="Welcome to Amazing Grace"
          title="Hospitality Textiles Designed for Quality & Comfort"
        />
        <Reveal delay={0.15}>
          <p className="mt-6 text-navy/70 text-base md:text-lg font-light leading-relaxed">
            Amazing Grace manufactures and supplies a comprehensive range of hospitality textiles and hotel essentials from Karamadai, Coimbatore, Tamil Nadu — serving hotels, resorts, hospitals and institutions across India, including Tamil Nadu, Kerala and Karnataka. Explore our{" "}
            <Link to="/products" className="text-gold underline-offset-4 hover:underline">hotel linen products</Link> and{" "}
            <Link to="/solutions" className="text-gold underline-offset-4 hover:underline">hospitality textile solutions</Link>, or{" "}
            <Link to="/contact" className="text-gold underline-offset-4 hover:underline">contact Amazing Grace</Link> for your requirement.
          </p>
          <Link
            to="/about"
            data-testid="intro-about-button"
            className="group inline-flex items-center gap-2 mt-10 border border-navy text-navy text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-navy hover:text-ivory"
          >
            About Amazing Grace
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <Reveal delay={0.2} className="relative">
        <div className="absolute -top-4 -right-4 w-full h-full border border-gold/50 pointer-events-none" />
        <div className="overflow-hidden group">
          <img
            src={IMG.bedroom2}
            alt="Premium hotel room with quality bed linen and bedding"
            loading="lazy"
            className="w-full h-[420px] md:h-[520px] object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

const ProductRange = () => (
  <section className="py-24 md:py-32 bg-linen/40">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <SectionHeader
        number="02"
        eyebrow="Collections"
        title="Our Product Range"
        description="A complete range of bedding, bath and hospitality essentials designed to meet the needs of modern hospitality businesses."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.1} className={i % 2 === 1 ? "lg:mt-12" : ""}>
            <Link
              to="/products"
              data-testid={`category-card-${c.id}`}
              className="group block bg-ivory border border-navy/10 h-full"
            >
              <div className="overflow-hidden h-60">
                <img
                  src={c.image}
                  alt={`${c.title} — hotel and hospitality textiles`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.05]"
                />
              </div>
              <div className="p-7">
                <span className="text-[10px] tracking-[0.3em] uppercase text-gold">0{i + 1}</span>
                <h3 className="font-serif text-2xl font-medium text-navy mt-2 group-hover:text-gold transition-colors duration-300">
                  {c.title}
                </h3>
                <p className="text-sm text-navy/60 font-light mt-3 leading-relaxed">{c.desc}</p>
                <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-navy/70 mt-5 group-hover:text-gold transition-colors duration-300">
                  View Collection <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CatalogueSection = () => (
  <section data-testid="home-catalogue-section" className="py-24 md:py-32 bg-navy">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div>
        <div className="flex items-center gap-4">
          <BookOpen size={22} strokeWidth={1.5} className="text-gold" />
          <span className="h-px w-10 bg-gold/60" />
          <span className="text-[11px] tracking-[0.32em] uppercase text-ivory/60">Product Catalogue</span>
        </div>
        <h2 className="font-serif font-medium tracking-tight text-4xl md:text-5xl text-ivory mt-6 leading-[1.05]">
          Explore Our Product <span className="italic text-gold">Catalogue</span>
        </h2>
        <p className="mt-6 text-ivory/70 text-base md:text-lg font-light leading-relaxed max-w-lg">
          Discover our complete range of hospitality textiles and hotel essentials — bed linen, duvets, pillows,
          mattresses, towels, robes, table textiles and more.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 mt-10">
          <Link
            to="/catalogue"
            data-testid="home-view-catalogue-btn"
            className="group inline-flex items-center justify-center gap-2 whitespace-nowrap bg-ivory text-navy text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-gold"
          >
            View Product Catalogue
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href="/Amazing-Grace-Product-Catalogue.pdf"
            download="Amazing-Grace-Product-Catalogue.pdf"
            data-testid="home-download-catalogue-btn"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap border border-ivory/50 text-ivory text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            <Download size={16} />
            Download Catalogue
          </a>
        </div>
      </div>
      <Reveal delay={0.2} className="relative hidden lg:block">
        <div className="absolute -top-4 -right-4 w-full h-full border border-gold/40 pointer-events-none" />
        <Link to="/catalogue" className="block overflow-hidden group">
          <img
            src="/catalogue/cover.jpg"
            alt="Amazing Grace product catalogue cover"
            className="w-full h-[440px] object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
          />
        </Link>
      </Reveal>
    </div>
  </section>
);

const TrustedBy = () => (
  <section data-testid="trusted-by-section" className="py-24 md:py-32 border-y border-navy/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <SectionHeader
        number="03"
        eyebrow="Our Customers"
        title="Trusted by Leading Hospitality Brands"
        description="We are delighted to serve and supply hospitality products to reputed names across the industry."
        align="center"
      />
      <div className="grid grid-cols-2 md:grid-cols-4 mt-16 border-t border-l border-navy/10">
        {CUSTOMERS.map((name, i) => (
          <Reveal key={name} delay={i * 0.05}>
            <div
              data-testid={`customer-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="group flex items-center justify-center h-32 md:h-40 border-r border-b border-navy/10 px-6 text-center transition-colors duration-500 hover:bg-navy"
            >
              <span className="font-serif text-xl md:text-2xl font-medium text-navy/80 group-hover:text-ivory transition-colors duration-500">
                {name}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2} className="text-center mt-12">
        <p className="text-navy/70 text-base md:text-lg font-light max-w-2xl mx-auto leading-relaxed">
          We are proud to support leading hospitality businesses with quality bedding, bath textiles and hospitality essentials — along with other reputed hospitality customers.
        </p>
        <p className="font-serif italic text-2xl text-navy mt-10">Want to Work With Us?</p>
        <Link
          to="/quote"
          data-testid="trusted-request-quote-button"
          className="group inline-flex items-center gap-2 mt-6 bg-navy text-ivory text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
        >
          Request a Quote
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const WhyChoose = () => (
  <section className="py-24 md:py-32">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <SectionHeader
        number="04"
        eyebrow="The Difference"
        title="Why Choose Amazing Grace"
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10 border border-navy/10 mt-16">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.07} className="h-full">
            <div
              data-testid={`why-card-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group bg-ivory p-10 h-full transition-colors duration-500 hover:bg-linen/50"
            >
              <w.icon size={26} strokeWidth={1.5} className="text-gold" />
              <h3 className="font-serif text-2xl font-medium text-navy mt-6">{w.title}</h3>
              <p className="text-sm text-navy/60 font-light mt-3 leading-relaxed">{w.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section data-testid="industries-section" className="py-24 md:py-32 bg-navy">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <SectionHeader
        number="05"
        eyebrow="Who We Serve"
        title="Serving the Hospitality & Institutional Industry"
        dark
      />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-ivory/10 border border-ivory/10 mt-16">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.label} delay={i * 0.04} className="h-full">
            <div
              data-testid={`industry-${ind.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group bg-navy p-8 h-full flex flex-col items-start gap-5 transition-colors duration-500 hover:bg-ink"
            >
              <ind.icon size={24} strokeWidth={1.5} className="text-gold" />
              <span className="font-serif text-lg md:text-xl text-ivory/90">{ind.label}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const BulkCTA = () => (
  <section data-testid="bulk-b2b-section" className="py-24 md:py-32 bg-linen/40 border-y border-navy/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="flex items-center gap-4">
          <span className="font-serif italic text-gold text-lg">06</span>
          <span className="h-px w-10 bg-gold/60" />
          <span className="text-[11px] tracking-[0.32em] uppercase text-navy/60">Bulk & B2B Orders</span>
        </div>
        <h2 className="font-serif font-medium tracking-tight text-4xl md:text-5xl text-navy mt-6 leading-[1.05]">
          Looking for Hospitality Textiles in <span className="italic text-gold">Bulk?</span>
        </h2>
      </div>
      <div>
        <p className="text-navy/70 text-base md:text-lg font-light leading-relaxed">
          Whether you are furnishing a new property, upgrading an existing hotel or sourcing hospitality textiles for your business, talk to our team about your requirements.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <Link
            to="/quote"
            data-testid="bulk-request-quote-button"
            className="group inline-flex items-center justify-center gap-2 bg-navy text-ivory text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
          >
            Request a Quote
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="bulk-talk-to-team-button"
            className="inline-flex items-center justify-center gap-2 border border-navy text-navy text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-navy hover:text-ivory"
          >
            Talk to Our Team
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => (
  <main data-testid="home-page">
    <Hero />
    <Intro />
    <EditorialMarquee />
    <ProductRange />
    <CatalogueSection />
    <TrustedBy />
    <Testimonials />
    <WhyChoose />
    <Industries />
    <BulkCTA />
    <CTASection
      title="Let's Build Better Hospitality Experiences Together."
      text="Connect with Amazing Grace for quality hospitality textiles and hotel essentials."
      image={IMG.lobby}
    />
  </main>
);

export default Home;
